/* eslint-disable import/no-extraneous-dependencies */
import { Link, useNavigate, useParams } from "react-router-dom";
import { useContext, useEffect, useState, useRef, ChangeEvent } from "react";
import * as Yup from "yup";
import { Form, Formik } from "formik";
import { enqueueSnackbar } from "notistack";
import { FiCamera, FiUser } from "react-icons/fi";

import { FormButton } from "../../../components/FormButton";
import { Input } from "../../../components/Input";
import { AuthContext } from "../../../contexts/AuthContext";
import { api } from "../../../services/api";
import { PageTitle } from "../../../components/PageTitle";
import { Profile } from "../../../types/permissions";

interface User {
  name: string;
  occupation: string;
  email: string;
  password: string;
  password_confirmation: string;
  avatar_url?: string | null;
}

export function UserEdit() {
  const { user } = useContext(AuthContext);
  const { id } = useParams();
  const [userProps, setUserProps] = useState<User>();
  const [avatarUrl, setAvatarUrl] = useState<string | null>(null);
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  const avatarInputRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();

  // Estados para perfis
  const [profiles, setProfiles] = useState<Profile[]>([]);
  const [selectedProfiles, setSelectedProfiles] = useState<string[]>([]);
  const [loadingProfiles, setLoadingProfiles] = useState(false);

  useEffect(() => {
    loadProfiles();
    if (id) {
      api.get(`users/show/${id}`).then(response => {
        setUserProps(response.data);
        setAvatarUrl(response.data.avatar_url);
      });
      loadUserProfiles();
    }
  }, [id]);

  async function loadProfiles() {
    try {
      setLoadingProfiles(true);
      const response = await api.get('/profiles');
      setProfiles(response.data);
    } catch (error: any) {
      enqueueSnackbar(
        error?.response?.data?.message || 'Erro ao carregar perfis',
        {
          variant: 'error',
          anchorOrigin: { vertical: 'top', horizontal: 'right' }
        }
      );
    } finally {
      setLoadingProfiles(false);
    }
  }

  async function loadUserProfiles() {
    if (!id) return;

    try {
      const response = await api.get(`/users/${id}/profiles`);
      const userProfiles = response.data || [];
      setSelectedProfiles(userProfiles.map((p: Profile) => p.id));
    } catch (error: any) {
      enqueueSnackbar(
        error?.response?.data?.message || 'Erro ao carregar perfis do usuário',
        {
          variant: 'error',
          anchorOrigin: { vertical: 'top', horizontal: 'right' }
        }
      );
    }
  }

  function toggleProfile(profileId: string) {
    setSelectedProfiles(prev => {
      if (prev.includes(profileId)) {
        return prev.filter(id => id !== profileId);
      }
      return [...prev, profileId];
    });
  }

  const handleAvatarChange = async (e: ChangeEvent<HTMLInputElement>) => {
    if (!id || !e.target.files || !e.target.files[0]) return;

    const file = e.target.files[0];

    if (!file.type.startsWith("image/")) {
      enqueueSnackbar("Por favor, selecione uma imagem válida.", {
        variant: "warning",
        anchorOrigin: { vertical: "top", horizontal: "right" },
      });
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      enqueueSnackbar("A imagem deve ter no máximo 5MB.", {
        variant: "warning",
        anchorOrigin: { vertical: "top", horizontal: "right" },
      });
      return;
    }

    setUploadingAvatar(true);

    try {
      const formData = new FormData();
      formData.append("avatar", file);

      const response = await api.patch(`/users/${id}/avatar`, formData);
      setAvatarUrl(response.data.avatar_url);

      enqueueSnackbar("Avatar atualizado com sucesso!", {
        variant: "success",
        anchorOrigin: { vertical: "top", horizontal: "right" },
      });
    } catch (error) {
      enqueueSnackbar("Erro ao atualizar avatar.", {
        variant: "error",
        anchorOrigin: { vertical: "top", horizontal: "right" },
      });
    } finally {
      setUploadingAvatar(false);
    }
  };

  function handleSubmit(data: User) {
    if (id) {
      api
        .put(`/users/update/${id}`, data)
        .then(async () => {
          // Atribuir perfis ao usuário
          try {
            await api.put(`/users/${id}/profiles`, {
              profile_ids: selectedProfiles,
            });
            enqueueSnackbar("Usuário editado com sucesso!", {
              variant: "success",
              anchorOrigin: {
                vertical: "top",
                horizontal: "right",
              },
            });
            navigate("/users");
          } catch (error: any) {
            enqueueSnackbar(
              error?.response?.data?.message || "Erro ao atualizar perfis do usuário",
              {
                variant: "warning",
                anchorOrigin: {
                  vertical: "top",
                  horizontal: "right",
                },
              }
            );
          }
        })
        .catch(({ response }) => {
          enqueueSnackbar(response.data.message, {
            variant: "warning",
            anchorOrigin: {
              vertical: "top",
              horizontal: "right",
            },
          });
        });
    } else {
      api
        .post("/users", data)
        .then(async (response) => {
          // Atribuir perfis ao novo usuário
          const userId = response.data.id;
          if (selectedProfiles.length > 0) {
            try {
              await api.put(`/users/${userId}/profiles`, {
                profile_ids: selectedProfiles,
              });
            } catch (error: any) {
              enqueueSnackbar(
                "Usuário criado, mas houve erro ao atribuir perfis",
                {
                  variant: "warning",
                  anchorOrigin: {
                    vertical: "top",
                    horizontal: "right",
                  },
                }
              );
            }
          }
          enqueueSnackbar("Usuário cadastrado com sucesso!", {
            variant: "success",
            anchorOrigin: {
              vertical: "top",
              horizontal: "right",
            },
          });
          navigate("/users");
        })
        .catch(({ response }) => {
          enqueueSnackbar(response.data.message, {
            variant: "warning",
            anchorOrigin: {
              vertical: "top",
              horizontal: "right",
            },
          });
        });
    }
  }

  const schema = Yup.object().shape({
    name: Yup.string().required("Campo nome é obrigatório"),
    occupation: Yup.string().required("Campo ocupação é obrigatório"),
    email: Yup.string()
      .email("E-mail inválido")
      .required("Campo e-mail é obrigatório"),
    // password: Yup.string().required("Campo senha é obrigatório"),
    password_confirmation: Yup.string().oneOf(
      [Yup.ref("password"), null],
      "Confirmação de senha está incorreta",
    ),
  });

  const initialValues = {
    name: userProps ? userProps.name : "",
    occupation: userProps ? userProps.occupation : "",
    email: userProps ? userProps.email : "",
    password: "",
    password_confirmation: "",
  };

  return (
    <div className="container mx-auto">
      <PageTitle
        title={id ? "Editar Usuário" : "Novo Usuário"}
        backButton
        backPath="/users"
      />
      <div className="w-full">
        <div className="bg-white rounded-lg shadow-sm p-6">
          <Formik
            initialValues={initialValues}
            enableReinitialize
            validationSchema={schema}
            onSubmit={values => {
              handleSubmit(values);
            }}
          >
            {({ errors }) => (
              <Form className="space-y-4">
                {/* Avatar Section - apenas na edição */}
                {id && (
                  <div className="flex justify-center mb-6">
                    <div className="relative">
                      <div className="w-24 h-24 rounded-full bg-gray-200 flex items-center justify-center overflow-hidden">
                        {avatarUrl ? (
                          <img
                            src={avatarUrl}
                            alt="Avatar"
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <FiUser size={40} className="text-gray-400" />
                        )}
                      </div>
                      <button
                        type="button"
                        onClick={() => avatarInputRef.current?.click()}
                        disabled={uploadingAvatar}
                        className="absolute bottom-0 right-0 w-8 h-8 bg-[#0085BD] rounded-full flex items-center justify-center text-white hover:bg-[#006a96] transition-colors disabled:opacity-50"
                        title="Alterar foto"
                      >
                        <FiCamera size={16} />
                      </button>
                      <input
                        ref={avatarInputRef}
                        type="file"
                        accept="image/*"
                        onChange={handleAvatarChange}
                        className="hidden"
                      />
                    </div>
                  </div>
                )}

                <div className="space-y-2">
                  <span className="text-sm text-[#0085BD] font-medium">
                    Nome
                  </span>
                  <Input
                    type="text"
                    name="name"
                    placeholder="Nome"
                    error={errors.name}
                    className="h-[52px] px-4 w-full rounded-lg text-[#7FA5B2] bg-white border border-gray-200 focus:border-[#0085BD] focus:ring-1 focus:ring-[#0085BD]"
                  />
                  <span className="text-sm text-[#0085BD] font-medium">
                    Ocupação
                  </span>
                  <Input
                    type="text"
                    name="occupation"
                    placeholder="Ocupação"
                    error={errors.occupation}
                    className="h-[52px] px-4 w-full rounded-lg text-[#7FA5B2] bg-white border border-gray-200 focus:border-[#0085BD] focus:ring-1 focus:ring-[#0085BD]"
                  />
                  <span className="text-sm text-[#0085BD] font-medium">
                    E-mail
                  </span>
                  <Input
                    type="text"
                    name="email"
                    placeholder="E-mail"
                    error={errors.email}
                    className="h-[52px] px-4 w-full rounded-lg text-[#7FA5B2] bg-white border border-gray-200 focus:border-[#0085BD] focus:ring-1 focus:ring-[#0085BD]"
                  />
                  <span className="text-sm text-[#0085BD] font-medium">
                    Senha
                  </span>
                  <Input
                    type="password"
                    name="password"
                    placeholder="Senha"
                    error={errors.password}
                    className="h-[52px] px-4 w-full rounded-lg text-[#7FA5B2] bg-white border border-gray-200 focus:border-[#0085BD] focus:ring-1 focus:ring-[#0085BD]"
                  />
                  <span className="text-sm text-[#0085BD] font-medium">
                    Confirme a senha
                  </span>
                  <Input
                    type="password"
                    name="password_confirmation"
                    placeholder="Confirme a senha"
                    error={errors.password_confirmation}
                    className="h-[52px] px-4 w-full rounded-lg text-[#7FA5B2] bg-white border border-gray-200 focus:border-[#0085BD] focus:ring-1 focus:ring-[#0085BD]"
                  />
                </div>

                {/* Seção de Perfis */}
                <div className="border-t pt-4 mt-6">
                  <h3 className="text-lg font-medium text-[#0085BD] mb-4">
                    Perfis de Acesso
                  </h3>
                  {loadingProfiles ? (
                    <div className="flex items-center justify-center py-8">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#0085BD]" />
                    </div>
                  ) : profiles.length === 0 ? (
                    <p className="text-sm text-gray-500">
                      Nenhum perfil disponível
                    </p>
                  ) : (
                    <div className="space-y-2">
                      {profiles.map((profile) => (
                        <label
                          key={profile.id}
                          className="flex items-start p-3 border rounded-lg cursor-pointer hover:bg-gray-50 transition-colors"
                        >
                          <input
                            type="checkbox"
                            checked={selectedProfiles.includes(profile.id)}
                            onChange={() => toggleProfile(profile.id)}
                            className="mt-1 h-4 w-4 text-[#0085BD] border-gray-300 rounded focus:ring-[#0085BD]"
                          />
                          <div className="ml-3 flex-1">
                            <div className="text-sm font-medium text-gray-900">
                              {profile.name}
                            </div>
                            <div className="text-xs text-gray-500">
                              {profile.description}
                            </div>
                          </div>
                        </label>
                      ))}
                    </div>
                  )}
                </div>

                <FormButton
                  title={id ? "Editar Usuário" : "Cadastrar Usuário"}
                  type="submit"
                />
              </Form>
            )}
          </Formik>
        </div>
      </div>
    </div>
  );
}
